import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, MoreHorizontalIcon, PlusIcon, ChevronRightIcon, BuildingIcon, UserIcon, LandmarkIcon, SmartphoneIcon, CreditCardIcon, WalletIcon } from 'lucide-react';
import { getTenant } from '../../../theme/tokens';
import { mockSuppliers } from '../mocks/mockSuppliers';
export function SupplierDetail() {
  const {
    t
  } = useTranslation('suppliers');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const supplier = mockSuppliers.find(s => s.id === id) || mockSuppliers[0];
  const fullAddress = [supplier.addressLine1, supplier.addressLine2, supplier.city, supplier.province, supplier.postcode, supplier.country].filter(Boolean).join(', ');
  const billingEmail = supplier.useAsBillingEmail ? supplier.email : `${supplier.email} (separate billing)`;
  const bills = {
    total: supplier.amountOwed !== `${getTenant().currency.code} 0.00` ? 1 : 0,
    due: supplier.amountOwed !== `${getTenant().currency.code} 0.00` ? 1 : 0,
    awaitingApproval: 0,
    scheduled: 0,
    paid: 0
  };
  const history = [{
    id: 'h1',
    action: `${supplier.name} was created`,
    date: supplier.dateCreated.replace(',', ' at')
  }];
  return <div className="space-y-8 max-w-7xl mx-auto pb-12" data-id="element-12833">
      {/* Header */}
      <div className="flex items-center" data-id="element-12834">
        <button onClick={() => navigate('/suppliers')} className="flex items-center text-gray-900 hover:text-gray-600 transition-colors text-lg font-semibold" data-id="element-12835">
          <ArrowLeftIcon size={20} className="mr-2" data-id="element-12836" />
          {t('pages.SupplierDetail.all_suppliers')}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-12837">
        {/* Left Column - Main Details */}
        <div className="lg:col-span-2 space-y-6" data-id="element-12838">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-12839">
            {/* Top Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-12840">
              <div className="flex justify-between items-start" data-id="element-12841">
                <div data-id="element-12842">
                  <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-12843">
                    {supplier.name}
                  </h1>
                  <div className="flex items-center text-sm" data-id="element-12844">
                    <span className="text-gray-500 mr-2" data-id="element-12845">
                      {t('pages.SupplierDetail.you_owe_them')}
                    </span>
                    <span className="px-2.5 py-0.5 bg-[#E2231A] text-white text-xs font-semibold rounded-md shadow-sm" data-id="element-12846">
                      {supplier.amountOwed}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-3" data-id="element-12847">
                  <button onClick={() => navigate(`/bill-form?supplierId=${supplier.id}`)} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-12848">
                    {t('pages.SupplierDetail.add_bill')}
                  </button>
                  <button aria-label="More actions" className="p-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-12849">
                    <MoreHorizontalIcon size={20} aria-hidden="true" data-id="element-12850" />
                  </button>
                </div>
              </div>
            </div>

            {/* Details Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-12851">
              <div className="flex justify-between items-center mb-6" data-id="element-12852">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-12853">
                  {t('pages.SupplierDetail.details')}
                </h3>
                <button onClick={() => navigate(`/supplier-form?id=${supplier.id}`)} className="text-sm font-medium text-[#9F140F] hover:text-[#8C6C00]" data-id="element-12854">
                  {t('pages.SupplierDetail.edit')}
                </button>
              </div>

              <div className="space-y-4" data-id="element-12855">
                {/* Type */}
                <div className="grid grid-cols-3 gap-4 border-b border-gray-50 pb-4" data-id="element-12856">
                  <div className="text-sm text-gray-500" data-id="element-12857">
                    {t('pages.BulkSupplierUpload.type')}
                  </div>
                  <div className="col-span-2" data-id="element-12858">
                    {supplier.supplierType === 'company' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100" data-id="element-12859">
                        <BuildingIcon size={12} className="mr-1.5" data-id="element-12860" />
                        {t('pages.SupplierDetail.company')}
                      </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100" data-id="element-12861">
                        <UserIcon size={12} className="mr-1.5" data-id="element-12862" />
                        {t('pages.SupplierDetail.individual')}
                      </span>}
                  </div>
                </div>

                {/* Company-specific fields */}
                {supplier.supplierType === 'company' && <>
                    <div className="grid grid-cols-3 gap-4" data-id="element-12863">
                      <div className="text-sm text-gray-500" data-id="element-12864">
                        {t('pages.SupplierDetail.company_name_local')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-12865">
                        {supplier.companyNameTh || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-12866">
                      <div className="text-sm text-gray-500" data-id="element-12867">
                        {t('pages.SupplierDetail.company_name_english')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-12868">
                        {supplier.companyNameEn || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-12869">
                      <div className="text-sm text-gray-500" data-id="element-12870">
                        {t('pages.SupplierDetail.tax_id_registration_no')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900 font-mono" data-id="element-12871">
                        {supplier.taxId || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-12872">
                      <div className="text-sm text-gray-500" data-id="element-12873">
                        {t('pages.SupplierDetail.branch')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-12874">
                        {supplier.branch === '00000' ? '00000 (Head Office)' : supplier.branch || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-12875">
                      <div className="text-sm text-gray-500" data-id="element-12876">
                        {t('pages.SupplierDetail.vat_registered')}
                      </div>
                      <div className="col-span-2" data-id="element-12877">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${supplier.vatRegistered ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`} data-id="element-12878">
                          {supplier.vatRegistered ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                    {supplier.contactPerson && <div className="grid grid-cols-3 gap-4" data-id="element-12879">
                        <div className="text-sm text-gray-500" data-id="element-12880">
                          {t('pages.SupplierDetail.contact_person')}
                        </div>
                        <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-12881">
                          {supplier.contactPerson}
                        </div>
                      </div>}
                  </>}

                {/* Individual-specific fields */}
                {supplier.supplierType === 'individual' && <>
                    <div className="grid grid-cols-3 gap-4" data-id="element-12882">
                      <div className="text-sm text-gray-500" data-id="element-12883">
                        {t('pages.SupplierDetail.full_name_thai')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-12884">
                        {supplier.fullNameTh || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-12885">
                      <div className="text-sm text-gray-500" data-id="element-12886">
                        {t('pages.SupplierDetail.full_name_english')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-12887">
                        {supplier.fullNameEn || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-12888">
                      <div className="text-sm text-gray-500" data-id="element-12889">
                        {t('pages.SupplierDetail.id_type')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900 capitalize" data-id="element-12890">
                        {supplier.idType === 'citizen_id' ? 'Citizen ID' : supplier.idType === 'passport' ? 'Passport' : '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-12891">
                      <div className="text-sm text-gray-500" data-id="element-12892">
                        {t('pages.SupplierDetail.id_number')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900 font-mono" data-id="element-12893">
                        {supplier.idNumber || '—'}
                      </div>
                    </div>
                    {supplier.individualTaxId && <div className="grid grid-cols-3 gap-4" data-id="element-12894">
                        <div className="text-sm text-gray-500" data-id="element-12895">
                          {t('pages.BulkSupplierUpload.tax_id')}
                        </div>
                        <div className="col-span-2 text-sm font-medium text-gray-900 font-mono" data-id="element-12896">
                          {supplier.individualTaxId}
                        </div>
                      </div>}
                  </>}

                {/* Shared fields */}
                <div className="grid grid-cols-3 gap-4 border-t border-gray-50 pt-4" data-id="element-12897">
                  <div className="text-sm text-gray-500" data-id="element-12898">
                    {t('pages.BulkSupplierUpload.email')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-12899">
                    {supplier.email}
                  </div>
                </div>
                {supplier.phone && <div className="grid grid-cols-3 gap-4" data-id="element-12900">
                    <div className="text-sm text-gray-500" data-id="element-12901">
                      {t('pages.BulkSupplierUpload.phone')}
                    </div>
                    <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-12902">
                      {supplier.phone}
                    </div>
                  </div>}
                <div className="grid grid-cols-3 gap-4" data-id="element-12903">
                  <div className="text-sm text-gray-500" data-id="element-12904">
                    {t('pages.SupplierDetail.billing_email')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-12905">
                    {billingEmail}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4" data-id="element-12906">
                  <div className="text-sm text-gray-500" data-id="element-12907">
                    {t('pages.SupplierDetail.address')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-12908">
                    {fullAddress || '—'}
                  </div>
                </div>
                {supplier.internalReference && <div className="grid grid-cols-3 gap-4" data-id="element-12909">
                    <div className="text-sm text-gray-500" data-id="element-12910">
                      {t('pages.SupplierDetail.internal_reference')}
                    </div>
                    <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-12911">
                      {supplier.internalReference}
                    </div>
                  </div>}
              </div>
            </div>

            {/* Pay-in Method Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-12912">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6" data-id="element-12913">
                {t('pages.SupplierDetail.payin_method')}
              </h3>

              <div className="bg-gray-50 rounded-lg p-5 border border-gray-200" data-id="element-12914">
                <div className="flex items-center" data-id="element-12915">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${supplier.payInMethod === 'acquiring' ? 'bg-green-100' : 'bg-blue-100'}`} data-id="element-12916">
                    {supplier.payInMethod === 'acquiring' ? <WalletIcon size={20} className="text-green-700" data-id="element-12917" /> : <CreditCardIcon size={20} className="text-blue-700" data-id="element-12918" />}
                  </div>
                  <div data-id="element-12919">
                    <div className="text-sm font-semibold text-gray-900" data-id="element-12920">
                      {supplier.payInMethod === 'acquiring' ? 'Acquiring Volume' : 'Card Payment'}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-12921">
                      {supplier.payInMethod === 'acquiring' ? 'Funded from merchant acquiring settlement balance' : 'Charged to linked card'}
                    </div>
                  </div>
                </div>

                {supplier.payInMethod === 'card' && supplier.cardNumber && <div className="grid grid-cols-2 gap-y-3 gap-x-6 mt-4 pt-4 border-t border-gray-200" data-id="element-12922">
                    <div data-id="element-12923">
                      <div className="text-xs text-gray-500 mb-1" data-id="element-12924">
                        {t('pages.SupplierDetail.cardholder')}
                      </div>
                      <div className="text-sm font-medium text-gray-900" data-id="element-12925">
                        {supplier.cardholderName}
                      </div>
                    </div>
                    <div data-id="element-12926">
                      <div className="text-xs text-gray-500 mb-1" data-id="element-12927">
                        {t('pages.SupplierDetail.card_number')}
                      </div>
                      <div className="text-sm font-medium text-gray-900 font-mono" data-id="element-12928">
                        {supplier.cardNumber}
                      </div>
                    </div>
                  </div>}
              </div>
            </div>

            {/* Payout Method Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-12929">
              <div className="flex justify-between items-center mb-6" data-id="element-12930">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-12931">
                  {t('pages.SupplierDetail.payout_method')}
                </h3>
                <button onClick={() => navigate(`/supplier-form?id=${supplier.id}`)} className="text-sm font-medium text-[#9F140F] hover:text-[#8C6C00]" data-id="element-12932">
                  {t('pages.SupplierDetail.edit_payout_method')}
                </button>
              </div>

              <div className="bg-gray-50 rounded-lg p-5 border border-gray-200" data-id="element-12933">
                {supplier.payoutMethod === 'bank_account' ? <>
                    <div className="flex items-center mb-4" data-id="element-12934">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-4" data-id="element-12935">
                        <LandmarkIcon size={20} className="text-green-700" data-id="element-12936" />
                      </div>
                      <div data-id="element-12937">
                        <div className="text-sm font-semibold text-gray-900" data-id="element-12938">
                          {t('pages.SupplierDetail.bank_account')}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-12939">
                          {t('pages.SupplierDetail.direct_transfer')}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-y-3 gap-x-6 mt-4 pt-4 border-t border-gray-200" data-id="element-12940">
                      <div data-id="element-12941">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-12942">
                          {t('pages.SupplierDetail.bank_name')}
                        </div>
                        <div className="text-sm font-medium text-gray-900" data-id="element-12943">
                          {supplier.bankName}
                        </div>
                      </div>
                      <div data-id="element-12944">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-12945">
                          {t('pages.SupplierDetail.account_number')}
                        </div>
                        <div className="text-sm font-medium text-gray-900 font-mono" data-id="element-12946">
                          {'***' + supplier.accountNumber.slice(-4)}
                        </div>
                      </div>
                      <div data-id="element-12947">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-12948">
                          {t('pages.SupplierDetail.account_name')}
                        </div>
                        <div className="text-sm font-medium text-gray-900" data-id="element-12949">
                          {supplier.accountName}
                        </div>
                      </div>
                    </div>
                  </> : <>
                    <div className="flex items-center mb-4" data-id="element-12950">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mr-4" data-id="element-12951">
                        <SmartphoneIcon size={20} className="text-purple-700" data-id="element-12952" />
                      </div>
                      <div data-id="element-12953">
                        <div className="text-sm font-semibold text-gray-900" data-id="element-12954">
                          {t('pages.SupplierDetail.promptpay')}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-12955">
                          {supplier.promptPayType === 'phone' ? 'Phone number' : supplier.promptPayType === 'citizen_id' ? 'Citizen ID' : 'Tax ID'}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-y-3 gap-x-6 mt-4 pt-4 border-t border-gray-200" data-id="element-12956">
                      <div data-id="element-12957">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-12958">
                          {t('pages.SupplierDetail.promptpay_id')}
                        </div>
                        <div className="text-sm font-medium text-gray-900 font-mono" data-id="element-12959">
                          {'***' + supplier.promptPayId.slice(-4)}
                        </div>
                      </div>
                      <div data-id="element-12960">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-12961">
                          {t('pages.SupplierDetail.display_name')}
                        </div>
                        <div className="text-sm font-medium text-gray-900" data-id="element-12962">
                          {supplier.promptPayName}
                        </div>
                      </div>
                    </div>
                  </>}
              </div>
            </div>

            {/* Source Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-12963">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6" data-id="element-12964">
                {t('pages.SupplierDetail.source')}
              </h3>
              <div className="space-y-4" data-id="element-12965">
                <div className="grid grid-cols-3 gap-4" data-id="element-12966">
                  <div className="text-sm text-gray-500" data-id="element-12967">
                    {t('pages.SupplierDetail.platform')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-12968">
                    {supplier.source}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4" data-id="element-12969">
                  <div className="text-sm text-gray-500" data-id="element-12970">
                    {t('pages.SupplierDetail.date_created')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-12971">
                    {supplier.dateCreated}
                  </div>
                </div>
              </div>
            </div>

            {/* Bills Section */}
            <div className="p-8" data-id="element-12972">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4" data-id="element-12973">
                {t('pages.SupplierDetail.bills')}
              </h3>
              <div className="border border-gray-200 rounded-lg p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors group" data-id="element-12974">
                <div data-id="element-12975">
                  <div className="text-sm font-medium text-gray-900 mb-1" data-id="element-12976">
                    {bills.total} {t('pages.SupplierDetail.bill')}
                    {bills.total !== 1 ? 's' : ''}{' '}
                    {t('pages.SupplierDetail.for')} {supplier.name}
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-12977">
                    {bills.due} {t('pages.SupplierDetail.due')}{' '}
                    {bills.awaitingApproval}{' '}
                    {t('pages.SupplierDetail.awaiting_approval')}{' '}
                    {bills.scheduled} {t('pages.SupplierDetail.scheduled_and')}{' '}
                    {bills.paid} {t('pages.SupplierDetail.paid')}
                  </div>
                </div>
                <ChevronRightIcon size={20} className="text-gray-400 group-hover:text-gray-600 transition-colors" data-id="element-12978" />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - History */}
        <div className="lg:col-span-1" data-id="element-12979">
          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-4" data-id="element-12980">
            {t('pages.SupplierDetail.history')}
          </h3>
          <div className="space-y-6" data-id="element-12981">
            {history.map((event, index) => <div key={event.id} className="relative flex gap-4" data-id="element-12982">
                {index !== history.length - 1 && <div className="absolute left-[9px] top-6 bottom-[-24px] w-px bg-gray-200" data-id="element-12983"></div>}
                <div className="relative z-10 flex-shrink-0 w-5 h-5 bg-gray-100 rounded flex items-center justify-center mt-0.5" data-id="element-12984">
                  <PlusIcon size={12} className="text-gray-600" data-id="element-12985" />
                </div>
                <div data-id="element-12986">
                  <p className="text-sm font-medium text-gray-900" data-id="element-12987">
                    {event.action}
                  </p>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-12988">{event.date}</p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}